import React, { useRef, useState, useEffect } from "react";
import { Loader2 } from "lucide-react";

interface AVSyncPlayerProps {
  videoSrc: string;
  audioSrc?: string;
  width?: number | string;
  height?: number | string;
  audioDelay?: number;
}

const AVSyncPlayer: React.FC<AVSyncPlayerProps> = ({
  videoSrc,
  audioSrc,
  width = "100%",
  height = "860px",
  audioDelay = 0,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [useNativeAudio, setUseNativeAudio] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const delayTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Reset player when source changes
    if (!videoSrc) return;

    const video = videoRef.current;
    const audio = audioRef.current;

    setIsLoading(true);
    setIsPlaying(false);

    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    if (delayTimeoutRef.current) {
      clearTimeout(delayTimeoutRef.current);
      delayTimeoutRef.current = null;
    }
  }, [videoSrc]);

  if (!videoSrc) {
    return (
      <div
        className="flex flex-col items-center justify-center p-8 text-center bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 w-full"
        style={{ height: height || "400px" }}
      >
        <div className="bg-white p-4 rounded-full shadow-sm mb-4">
          <Loader2 className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Video Available
        </h3>
        <p className="text-sm text-gray-600 max-w-xs leading-relaxed">
          There is no video URL from this source. Please try toggling to the
          next source which could be frontend or backend.
        </p>
      </div>
    );
  }

  const hasExternalAudio = Boolean(audioSrc);

  const handlePlayPause = () => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (!video) return;

    if (isPlaying) {
      video.pause();
      if (hasExternalAudio && audio) {
        audio.pause();
      }

      if (delayTimeoutRef.current) {
        clearTimeout(delayTimeoutRef.current);
        delayTimeoutRef.current = null;
      }
      setIsPlaying(false);
    } else {
      video.play().catch((e) => console.warn("Video play blocked:", e));

      if (hasExternalAudio && audio && !useNativeAudio) {
        if (delayTimeoutRef.current) {
          clearTimeout(delayTimeoutRef.current);
        }

        const startAudio = () => {
          if (!audio || !video) return;
          audio.currentTime = Math.max(video.currentTime - audioDelay, 0);
          audio.play().catch((e) => console.warn("Audio play blocked:", e));
        };

        if (audioDelay > 0) {
          delayTimeoutRef.current = setTimeout(startAudio, audioDelay * 1000);
        } else {
          startAudio();
        }
      }

      setIsPlaying(true);
    }
  };

  const handleToggleNativeAudio = () => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (useNativeAudio) {
      // Switching back to external audio
      if (audio && isPlaying) {
        audio.currentTime = Math.max((video?.currentTime || 0) - audioDelay, 0);
        audio.play().catch((e) => console.warn("Audio play blocked:", e));
      }
      setUseNativeAudio(false);
    } else {
      // Switching to native audio
      if (audio) {
        audio.pause();
      }
      setUseNativeAudio(true);
    }
  };

  const handleSeeked = () => {
    if (
      hasExternalAudio &&
      audioRef.current &&
      videoRef.current &&
      !useNativeAudio
    ) {
      audioRef.current.currentTime = Math.max(
        videoRef.current.currentTime - audioDelay,
        0,
      );
    }
  };

  return (
    <div className="flex flex-col items-center space-y-1">
      <div className="relative w-full overflow-hidden">
        <video
          key={videoSrc}
          ref={videoRef}
          src={videoSrc}
          width={width}
          height={height}
          muted={hasExternalAudio && !useNativeAudio}
          onSeeked={handleSeeked}
          onLoadedData={() => setIsLoading(false)}
          onCanPlay={() => setIsLoading(false)}
          onLoadStart={() => setIsLoading(true)}
          style={{
            borderRadius: "12px",
            opacity: isLoading ? 0 : 1,
            transition: "opacity 0.2s",
          }}
          controls={!hasExternalAudio || useNativeAudio}
        />

        {isLoading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg z-50 w-full h-full">
            <div className="flex flex-col items-center gap-2 text-white">
              <Loader2 className="w-10 h-10 animate-spin text-blue-400" />
              <span className="text-base font-semibold tracking-wide">
                Loading Video...
              </span>
            </div>
          </div>
        )}

        {hasExternalAudio && !useNativeAudio && (
          <button
            onClick={handlePlayPause}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg z-20"
          >
            {isPlaying ? "Pause" : "Play"}
          </button>
        )}
      </div>

      {hasExternalAudio && (
        <div className="flex flex-col items-center gap-2 mt-2">
          <p className="text-sm text-gray-600 text-center">
            If the audio isn't playing, click below for native candidate audio.
            <br />
            <span className="text-xs text-gray-400 italic">
              (Note: Native audio does not include AI interviewer responses)
            </span>
          </p>
          <button
            onClick={handleToggleNativeAudio}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${useNativeAudio
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
          >
            {useNativeAudio ? "Using Native Audio" : "Use Native Audio"}
          </button>
        </div>
      )}

      {hasExternalAudio && (
        <audio ref={audioRef} src={audioSrc} muted={useNativeAudio} />
      )}
    </div>
  );
};

export default AVSyncPlayer;
